import { useRef, useState } from 'react';
import { bt, btp } from '../i18n';
import { useBuilderStore } from '../state/builderStore';
import { useInertWhenReadOnly } from '../shell/readOnlyInert';
import { FieldControl } from '../inspector/FieldControl';
import { ChevronDownIcon, ChevronRightIcon } from '../ui/icons';
import { ThemeColorControl } from './ThemeColorControl';
import { THEME_SCHEMA, type ThemeFieldEntry, type ThemeGroup } from './themeSchema';
import { contrastVerdict, WCAG_AA_NORMAL } from './contrast';
import type { ImageValue, ThemeSettings } from '@aquilixshop/ui-react';
import type { ThemeSaveState, ThemeSettingsPatch } from '../state/builderStore';

/**
 * The REAL global Theme-settings editor (feature 0019 Slice 2) — replaces the old `aria-disabled` stub.
 *
 * Data-driven from {@link THEME_SCHEMA}: colors (pure-hex `ThemeColorControl`), gradients/typography/
 * layout (the shared inspector controls via `FieldControl`). Every edit calls `updateThemeSettings`, which
 * re-themes the canvas live and persists through the DEDICATED versionless theme channel — never the page
 * autosave. The panel states that changes are LIVE (Q1) and shows a non-blocking WCAG contrast warning
 * (finding #16).
 */
export function ThemeSettingsPanel() {
  const theme = useBuilderStore((s) => s.themeSettings);
  const update = useBuilderStore((s) => s.updateThemeSettings);
  const saveState = useBuilderStore((s) => s.themeSaveState);
  // Consultation: the theme is read, never retuned — `updateThemeSettings` already refuses, this is so
  // the colour pickers don't open on a value they cannot change.
  const listRef = useRef<HTMLDivElement>(null);
  useInertWhenReadOnly(listRef);

  return (
    <div className="bld-themepanel" aria-label={bt('chrome.tabs.theme', 'Theme settings')}>
      <p className="bld-themepanel__head">{bt('chrome.tabs.theme', 'Theme settings')}</p>

      <div className="bld-themenote" role="note">
        <span className="bld-themenote__dot" aria-hidden="true" />
        <span>
          {bt('chrome.theme.live_note', 'Theme changes apply to your live store immediately.')}
          <ThemeSaveStatus state={saveState} />
        </span>
      </div>

      <div className="bld-themepanel__list" ref={listRef}>
        {THEME_SCHEMA.map((group) => (
          <ThemeGroupSection key={group.title} group={group} theme={theme} update={update} />
        ))}
      </div>
    </div>
  );
}

/** The tiny "Saving…/Saved/…" status appended to the live-on-save note. */
function ThemeSaveStatus({ state }: { state: ThemeSaveState }) {
  if (state === 'idle') return null;
  const text =
    state === 'saving'
      ? bt('chrome.theme.saving', 'Saving…')
      : state === 'saved'
        ? bt('chrome.theme.saved', 'Saved.')
        : state === 'offline'
          ? bt('chrome.theme.offline', 'Offline — your change will save when you reconnect.')
          : bt('chrome.theme.save_failed', 'Couldn’t save — it will retry on your next change.');
  return (
    <>
      {' '}
      <span className={`bld-themenote__status bld-themenote__status--${state}`} role="status">
        {text}
      </span>
    </>
  );
}

/** One collapsible group (Color palette / Typography / Layout) — open by default (theme editing is the task). */
function ThemeGroupSection({
  group,
  theme,
  update,
}: {
  group: ThemeGroup;
  theme: ThemeSettings;
  update: (patch: ThemeSettingsPatch) => void;
}) {
  const [open, setOpen] = useState(true);
  return (
    <div className="bld-themegroup">
      <button
        type="button"
        className="bld-themegroup__head"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        <span className="bld-themegroup__title">{themeGroupTitle(group.title)}</span>
        <span className="bld-themegroup__chev dir-flip" aria-hidden="true">
          {open ? <ChevronDownIcon size={16} /> : <ChevronRightIcon size={16} />}
        </span>
      </button>
      {open && (
        <div className="bld-themegroup__body">
          {group.entries.map((entry) => (
            <ThemeFieldRow
              key={entry.field.id}
              entry={translateThemeEntry(entry)}
              theme={theme}
              update={update}
            />
          ))}
        </div>
      )}
    </div>
  );
}

/** Dispatch one schema entry to its control + append the contrast warning under the solid-button label. */
function ThemeFieldRow({
  entry,
  theme,
  update,
}: {
  entry: ThemeFieldEntry;
  theme: ThemeSettings;
  update: (patch: ThemeSettingsPatch) => void;
}) {
  switch (entry.kind) {
    case 'color':
      return (
        <>
          <ThemeColorControl
            id={entry.field.id}
            label={entry.field.label}
            info={entry.field.info}
            value={entry.get(theme)}
            onChange={(hex) => update(entry.patch(hex))}
          />
          {entry.contrastAgainstAccent && <ContrastWarning theme={theme} />}
        </>
      );
    case 'gradient':
      return (
        <FieldControl
          field={entry.field}
          value={entry.get(theme)}
          onChange={(v) => update(entry.patch((v as string | undefined) ?? ''))}
        />
      );
    case 'image':
      return (
        <FieldControl
          field={entry.field}
          value={entry.get(theme)}
          onChange={(v) => update(entry.patch(v as ImageValue | undefined))}
        />
      );
    case 'select':
      return (
        <FieldControl
          field={entry.field}
          value={entry.get(theme)}
          onChange={(v) => update(entry.patch(String(v ?? ''), theme))}
        />
      );
    case 'range':
      return (
        <FieldControl
          field={entry.field}
          value={entry.get(theme)}
          onChange={(v) => update(entry.patch(Number(v)))}
        />
      );
    case 'text':
      return (
        <FieldControl
          field={entry.field}
          value={entry.get(theme)}
          onChange={(v) => update(entry.patch(String(v ?? '')))}
        />
      );
    case 'checkbox':
      return (
        <FieldControl
          field={entry.field}
          value={entry.get(theme)}
          onChange={(v) => update(entry.patch(Boolean(v)))}
        />
      );
    default: {
      const _exhaustive: never = entry;
      return _exhaustive;
    }
  }
}

/** Live, non-blocking WCAG-AA warning for the solid-button label vs Accent 1 (finding #16). */
function ContrastWarning({ theme }: { theme: ThemeSettings }) {
  const verdict = contrastVerdict(theme.colors.solidButtonLabel, theme.colors.accent1);
  if (!verdict || verdict.passesAA) return null;
  return (
    <p className="bld-theme-warn" role="status">
      {btp(
        'chrome.theme.contrast_warn',
        'Low contrast — the solid button label against Accent 1 is {ratio}:1, below WCAG AA ({min}:1). Button text may be hard to read.',
        { ratio: verdict.ratio.toFixed(1), min: WCAG_AA_NORMAL },
      )}
    </p>
  );
}

/** A group heading, keyed by its English title (`Color palette` → `chrome.theme.group.color_palette`). */
function themeGroupTitle(title: string): string {
  return bt(`chrome.theme.group.${title.toLowerCase().replace(/ /g, '_')}`, title);
}

/**
 * The theme schema is authored in English, like the section schemas — and translated at the same
 * seam, by a key DERIVED from the field id (`theme.colors.accent1`, `theme.colors.accent1.info`).
 * Only display text moves: `id`, `get`, `patch` and every option `value` are copied through untouched.
 * A font's name is a proper noun and stays; only the generic "System UI" has an entry.
 */
function translateThemeEntry(entry: ThemeFieldEntry): ThemeFieldEntry {
  const label = bt(entry.field.id, entry.field.label);
  const info =
    entry.field.info !== undefined ? bt(`${entry.field.id}.info`, entry.field.info) : undefined;
  switch (entry.kind) {
    case 'color':
      return {
        ...entry,
        field: { ...entry.field, label, ...(info !== undefined ? { info } : {}) },
      };
    case 'gradient':
      return {
        ...entry,
        field: { ...entry.field, label, ...(info !== undefined ? { info } : {}) },
      };
    case 'image':
      return {
        ...entry,
        field: { ...entry.field, label, ...(info !== undefined ? { info } : {}) },
      };
    case 'range':
      return {
        ...entry,
        field: { ...entry.field, label, ...(info !== undefined ? { info } : {}) },
      };
    case 'select':
      return {
        ...entry,
        field: {
          ...entry.field,
          label,
          ...(info !== undefined ? { info } : {}),
          options: entry.field.options.map((o) => ({
            ...o,
            label: bt(
              `${entry.optionKeyPrefix}.${o.label.toLowerCase().replace(/ /g, '_')}`,
              o.label,
            ),
          })),
        },
      };
    case 'text':
      return {
        ...entry,
        field: {
          ...entry.field,
          label,
          ...(info !== undefined ? { info } : {}),
          // The PLACEHOLDER is the translated default label the storefront would write anyway, so it has
          // to follow the builder's language too — an empty "High-demand text" field must show the seller
          // the words her shoppers will actually see.
          ...(entry.field.placeholder !== undefined
            ? { placeholder: bt(`${entry.field.id}.placeholder`, entry.field.placeholder) }
            : {}),
        },
      };
    case 'checkbox':
      return {
        ...entry,
        field: { ...entry.field, label, ...(info !== undefined ? { info } : {}) },
      };
    default: {
      const _exhaustive: never = entry;
      return _exhaustive;
    }
  }
}
